import React, { useState } from "react";
import styled from "styled-components";
import {
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { Delete, InfoOutlined } from "@mui/icons-material";
import { StyledErrorMessage } from "../style/modalStyle";
import { StyledLink } from "../style/ititn/itinUpload";
import PassportInstructionModal from "../itin/PassportInstructionModal";
import DocumentInstructorModel from "../tools/DocumentInstructorModel";
import { EyeIcon } from "../../assets/Icon/dashboredIcon";

interface FilePropType {
  title?: string;
  icon?: React.ReactNode;
  desc?: string;
  name?: string;
  formik?: any;
  handleFileChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  filePreviews: string[];
  setFilePreviews: React.Dispatch<React.SetStateAction<string[]>>;
  setFileUrl?: (data: any) => void;
  setOpenPreview?: (data: any) => void;
  disabled?: boolean;
  accept?: string;
  isEyeIcon?: boolean;
  showInstruction?: boolean;
  placeholder?: string;
  modeltitle?: string;
  showMoreButton?: boolean;
  showBackground?: boolean;
  onDelete?: (id: number) => void;
  documents?: any[];
  setDocuments?: React.Dispatch<React.SetStateAction<any[]>>;
  showBottomInfo?: boolean;
}

const data = [
  {
    // instructions: [
    //  "Store all company documents in the shared folder with proper names and avoid sharing them outside without permission."
    // ],
    instructions: [
      "Please upload all the business incorporation documents that you have, not EIN. Only formation documents with the state.",
    ],
  },
];

const UploadMultipleFile = (props: FilePropType) => {
  const {
    title,
    icon,
    name,
    formik,
    filePreviews,
    setFilePreviews,
    setOpenPreview,
    setFileUrl,
    modeltitle,
    disabled,
    accept,
    isEyeIcon = false,
    showInstruction,
    placeholder,
    showMoreButton,
    showBackground,
    onDelete,
    documents,
    setDocuments,
    showBottomInfo,
  } = props;

  const fieldName = name || "file-upload";
  const error = formik?.touched[fieldName] && formik?.errors[fieldName];
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const parsedAccept = accept
    ? accept.split(",").reduce((acc: any, type) => {
        const trimmed = type.trim();
        if (!acc[trimmed]) acc[trimmed] = [];
        return acc;
      }, {})
    : {
        // "image/jpeg": [".jpeg", ".jpg"],
        // "image/png": [".png"],
        "application/pdf": [".pdf"],
        "application/msword": [".doc"],
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          [".docx"],
      };

  const { getRootProps, getInputProps } = useDropzone({
    accept: parsedAccept,
    multiple: true,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        setUploadError("One or more files are of invalid type.");
        return;
      }

      if (acceptedFiles.length > 0) {
        const newFiles = acceptedFiles;
        const currentFiles = documents || formik?.values[fieldName] || [];

        if (setDocuments) {
          setDocuments((prev) => [...prev, ...newFiles]);
        } else if (formik) {
          formik.setFieldValue(fieldName, [...currentFiles, ...newFiles]);
        }
        setFilePreviews((prev) => [...prev, ...newFiles.map((f) => f.name)]);
        setUploadError(null);
      }
    },
  });

  const removeFile = (index: number) => {
    const newFiles = [...(documents || formik?.values[fieldName] || [])];
    const newPreviews = [...filePreviews];

    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);

    if (setDocuments) {
      setDocuments(newFiles);
    } else if (formik) {
      formik.setFieldValue(fieldName, newFiles);
    }
    setFilePreviews(newPreviews);
  };

  const showMoreFiles = showMore ? filePreviews : filePreviews.slice(0, 1);
  return (
    <>
      <Wrapper
        style={{
          backgroundColor: showBackground ? "#ffffff" : "transparent",
          border: error ? "1px solid red" : "1px solid transparent",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1px solid transparent",
          }}
        >
          <Title variant="h4">{title}</Title>
          {showInstruction && (
            <StyledLink to="" onClick={() => setIsModalOpen(true)}>
              {modeltitle ? modeltitle : "Passport Instruction"}
            </StyledLink>
          )}
        </div>

        <DropArea {...getRootProps()}>
          <input {...getInputProps()} disabled={disabled} />
          <UploadContainer>
            {icon}
            <StyledTypography>
              {placeholder !== "" ? (
                placeholder
              ) : title !== "Scan Passport" ? (
                <>
                  <span>Click here</span> or drag & drop your file(s)
                </>
              ) : (
                "Scan your passport"
              )}
            </StyledTypography>
          </UploadContainer>
        </DropArea>
        {showBottomInfo && (
          <div
            style={{
              display: "flex",
              justifyContent: "end",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                marginTop: "10px",
              }}
            >
              <InfoOutlined fontSize="small" htmlColor="#2977cc" />
              <p
                style={{ fontSize: "14px", color: "#363636", fontWeight: 500 }}
              >
                Please upload the formation document first
              </p>
            </div>
          </div>
        )}
        {showMoreButton ? (
          <>
            {showMoreFiles.length > 0 &&
              showMoreFiles.map((name, index) => (
                <PreviewContainer key={index}>
                  <Typography
                    sx={{
                      overflowX: "auto",
                      width: isTablet ? "300px" : isMobile ? "150px" : "100%",
                    }}
                  >
                    {name.split("/").pop() || name}
                  </Typography>
                  <Typography
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {isEyeIcon && (
                      <button
                        type="button"
                        style={{
                          background: "none",
                          border: "none",
                          paddingTop: "5px",
                        }}
                        onClick={() => {
                          let finalUrl = "";

                          // CASE 1: Backend file
                          if (documents?.length && index < documents.length) {
                            finalUrl = documents[index]?.fileUrl;
                          }
                          // CASE 2: Newly uploaded file
                          else {
                            const file = formik.values[fieldName][index];
                            if (!file) return;
                            console.log(file, "file");
                            const blobUrl = URL.createObjectURL(file);

                            finalUrl = blobUrl;
                          }

                          if (finalUrl) {
                            setFileUrl?.(finalUrl);
                            setOpenPreview?.(true);
                          }
                        }}
                      >
                        <EyeIcon />
                      </button>
                    )}

                    <IconButton
                      onClick={() => {
                        if (documents?.length && index < documents?.length) {
                          onDelete && onDelete(index);
                        } else {
                          removeFile(index);
                        }
                      }}
                      disabled={disabled}
                    >
                      <Delete color={disabled ? "disabled" : "error"} />
                    </IconButton>
                  </Typography>
                </PreviewContainer>
              ))}
            {filePreviews.length > 1 && (
              <Button onClick={() => setShowMore(!showMore)}>
                {showMore ? "Show Less" : "Show More"}
              </Button>
            )}
          </>
        ) : (
          <>
            {filePreviews.length > 0 &&
              filePreviews.map((name, index) => (
                <PreviewContainer key={index}>
                  <Typography
                    sx={{
                      overflowX: "auto",
                    }}
                  >
                    {name}
                  </Typography>
                  <IconButton
                    onClick={() => removeFile(index)}
                    disabled={disabled}
                  >
                    <Delete color="error" />
                  </IconButton>
                </PreviewContainer>
              ))}
          </>
        )}
      </Wrapper>

      {uploadError || error ? (
        <StyledErrorMessage>{uploadError || error}</StyledErrorMessage>
      ) : (
        <></>
      )}

      <DocumentInstructorModel
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        title="Company Documents Instructions"
        data={data}
      />
    </>
  );
};

export default UploadMultipleFile;

const Wrapper = styled.div`
  padding: 24px;
  border-radius: 10px;
  box-shadow: 0px 2px 40px 0px #dde3ea33;
`;
const Title = styled(Typography)`
  font-size: 16px !important;
  font-weight: 500 !important;
  line-height: 20px !important;
`;
const DropArea = styled.div`
  margin-top: 10px;
  border: 0.6px dashed #2977cc;
  border-radius: 10px;
  padding: 40px;
  background: #eaf1fa;
  text-align: center;
  cursor: pointer;
`;
const UploadContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-direction: column;
  cursor: pointer;
`;
const StyledTypography = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  color: #2c3034;
  span {
    font-size: 14px;
    font-weight: 600;
    color: #2977cc;
  }
  overflow-x: auto;
`;
const PreviewContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 20px;
  padding: 10px;
  margin-top: 10px;
  border-radius: 5px;
  background: #f5f5f5;
  .file-preview {
    max-width: 100px;
    max-height: 100px;
    border-radius: 5px;
  }
`;
